/**
 * depthOverrides.js — User depth chart overrides for the Projection Lab
 *
 * Storage model:
 *   mock1_depth_overrides_{abbrev}        JSON map of { slotKey: sleeperId }
 *   mock1_depth_overrides_version         integer (DEPTH_OVERRIDE_VERSION)
 *
 * On load, a stored version that does not match DEPTH_OVERRIDE_VERSION drops
 * all overrides across every team. getStarters() merges overrides into base
 * starters by matching sleeper_id directly against per-slot annotations on
 * STARTERS_2025 (with PLAYERS-keyed name fallback for legacy/missing-id
 * cases), then reslotting the original base entry (preserving games/stats/
 * pos) under the override slot key. Stale ids that no longer resolve to a
 * base player at the matching position are silently dropped and the cleaned
 * override is persisted.
 */

import { STARTERS_2025 } from "../data/projectionData";
import { PLAYERS } from "../data/players";

export const DEPTH_OVERRIDE_VERSION = 1;

const STORAGE_PREFIX = "mock1_depth_overrides_";
const VERSION_KEY = "mock1_depth_overrides_version";

// ── localStorage helpers (try/catch for quota / private browsing) ────────────

function safeGet(key) {
  try { return localStorage.getItem(key); }
  catch { return null; }
}

function safeSet(key, value) {
  try { localStorage.setItem(key, value); }
  catch { /* quota exceeded or private browsing */ }
}

function safeRemove(key) {
  try { localStorage.removeItem(key); }
  catch { /* ignore */ }
}

function dropAllOverrides() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach(safeRemove);
  } catch { /* ignore */ }
}

// ── Public API ───────────────────────────────────────────────────────────────

export function loadDepthOverrides(abbrev) {
  const storedRaw = safeGet(VERSION_KEY);
  const storedNum = storedRaw == null ? null : parseInt(storedRaw, 10);
  if (storedNum !== DEPTH_OVERRIDE_VERSION) {
    dropAllOverrides();
    safeSet(VERSION_KEY, String(DEPTH_OVERRIDE_VERSION));
    return null;
  }
  const raw = safeGet(STORAGE_PREFIX + abbrev);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed;
  } catch { return null; }
}

export function saveDepthOverrides(abbrev, overrides) {
  if (!overrides || typeof overrides !== "object") return;
  safeSet(VERSION_KEY, String(DEPTH_OVERRIDE_VERSION));
  if (Object.keys(overrides).length === 0) {
    safeRemove(STORAGE_PREFIX + abbrev);
    return;
  }
  safeSet(STORAGE_PREFIX + abbrev, JSON.stringify(overrides));
}

export function clearDepthOverrides(abbrev) {
  safeRemove(STORAGE_PREFIX + abbrev);
}

// ── Merge logic ──────────────────────────────────────────────────────────────

const SLOT_RE = /^(QB|RB|WR|TE)(\d+)$/;

function parseSlot(slotKey) {
  const m = SLOT_RE.exec(slotKey);
  if (!m) return null;
  return { pos: m[1], idx: parseInt(m[2], 10) };
}

function normalizeName(n) {
  if (!n) return "";
  return n
    .replace(/[.’']/g, "")
    .replace(/\s+(Jr|Sr|II|III|IV)$/i, "")
    .trim()
    .toLowerCase();
}

let _sleeperToName = null;
function getSleeperToName() {
  if (_sleeperToName) return _sleeperToName;
  const map = new Map();
  for (const p of PLAYERS) {
    if (p && p.sleeper_id && p.name) map.set(String(p.sleeper_id), p.name);
  }
  _sleeperToName = map;
  return map;
}

let _nameToSleeper = null;
function getNameToSleeper() {
  if (_nameToSleeper) return _nameToSleeper;
  const map = new Map();
  for (const p of PLAYERS) {
    if (p && p.sleeper_id && p.name) {
      map.set(normalizeName(p.name), String(p.sleeper_id));
    }
  }
  _nameToSleeper = map;
  return map;
}

/**
 * Resolve a player name to a sleeper_id.
 *
 * Resolution order:
 *   1. If `abbrev` is given, scan STARTERS_2025[abbrev] for a slot whose
 *      normalized player name matches `name` and read its annotated
 *      sleeper_id. This is the primary source post genStartersSleeperIds.js
 *      (550 of 552 slots covered).
 *   2. Fall back to a PLAYERS-keyed lazy map. Catches players who aren't on
 *      the team passed via `abbrev`, and the 2 STARTERS_2025 slots that
 *      lack a sleeper_id annotation if they happen to also be in PLAYERS.
 *
 * Returns null if neither path resolves.
 */
export function nameToSleeperId(name, abbrev) {
  const key = normalizeName(name);
  if (!key) return null;

  if (abbrev) {
    const team = STARTERS_2025[abbrev];
    if (team) {
      for (const slotKey of Object.keys(team)) {
        const entry = team[slotKey];
        if (!entry?.player) continue;
        if (normalizeName(entry.player) !== key) continue;
        if (entry.sleeper_id) return String(entry.sleeper_id);
        // Name matched on this team but slot has no sleeper_id annotation —
        // fall through to PLAYERS fallback rather than returning null.
        break;
      }
    }
  }

  return getNameToSleeper().get(key) ?? null;
}

function findBaseEntryBySleeperId(baseTeam, pos, sleeperId) {
  const target = String(sleeperId);
  for (const slotKey of Object.keys(baseTeam)) {
    const slot = parseSlot(slotKey);
    if (!slot || slot.pos !== pos) continue;
    const entry = baseTeam[slotKey];
    if (entry?.sleeper_id && String(entry.sleeper_id) === target) {
      return { slotKey, entry };
    }
  }
  return null;
}

function findBaseEntryByName(baseTeam, pos, name) {
  const target = normalizeName(name);
  if (!target) return null;
  for (const slotKey of Object.keys(baseTeam)) {
    const slot = parseSlot(slotKey);
    if (!slot || slot.pos !== pos) continue;
    const entry = baseTeam[slotKey];
    if (entry?.player && normalizeName(entry.player) === target) {
      return { slotKey, entry };
    }
  }
  return null;
}

function basePosSlotsSorted(baseTeam, pos) {
  const out = [];
  for (const slotKey of Object.keys(baseTeam)) {
    const slot = parseSlot(slotKey);
    if (slot && slot.pos === pos) out.push({ slotKey, idx: slot.idx, entry: baseTeam[slotKey] });
  }
  out.sort((a, b) => a.idx - b.idx);
  return out;
}

function mergeOverrides(base, overrides) {
  const sleeperToName = getSleeperToName();

  const overrideByPos = {};
  for (const slotKey of Object.keys(overrides)) {
    const slot = parseSlot(slotKey);
    if (!slot) continue;
    if (!overrideByPos[slot.pos]) overrideByPos[slot.pos] = [];
    overrideByPos[slot.pos].push({ slotKey, idx: slot.idx, sleeperId: String(overrides[slotKey]) });
  }
  for (const pos of Object.keys(overrideByPos)) {
    overrideByPos[pos].sort((a, b) => a.idx - b.idx);
  }

  const merged = { ...base };
  const cleanedOverrides = {};
  let cleanupHappened = false;

  for (const pos of Object.keys(overrideByPos)) {
    const overrideEntries = overrideByPos[pos];
    const baseSlotsForPos = basePosSlotsSorted(base, pos);

    const orderedEntries = [];
    const usedBaseKeys = new Set();
    const resolvedIds = [];

    for (const oe of overrideEntries) {
      // Prefer STARTERS_2025-direct lookup (sleeper_id → slot match on this
      // team's annotated entries).
      let found = findBaseEntryBySleeperId(base, pos, oe.sleeperId);
      if (!found) {
        // Fallback: PLAYERS-keyed sleeper_id → name → base lookup. Catches
        // override ids written before STARTERS_2025 carried sleeper_id, or
        // any id whose corresponding slot in base lacks an annotation.
        const name = sleeperToName.get(oe.sleeperId);
        if (name) found = findBaseEntryByName(base, pos, name);
      }
      if (!found) { cleanupHappened = true; continue; }
      orderedEntries.push(found.entry);
      usedBaseKeys.add(found.slotKey);
      resolvedIds.push(oe.sleeperId);
    }

    for (const bs of baseSlotsForPos) {
      if (!usedBaseKeys.has(bs.slotKey)) orderedEntries.push(bs.entry);
    }

    for (const bs of baseSlotsForPos) delete merged[bs.slotKey];
    orderedEntries.forEach((entry, i) => {
      merged[`${pos}${i + 1}`] = entry;
    });

    resolvedIds.forEach((id, i) => {
      cleanedOverrides[`${pos}${i + 1}`] = id;
    });
  }

  return { merged, cleanedOverrides, cleanupHappened };
}

export function getStarters(abbrev) {
  const base = STARTERS_2025[abbrev];
  if (!base) return {};

  const overrides = loadDepthOverrides(abbrev);
  if (!overrides || Object.keys(overrides).length === 0) return base;

  const { merged, cleanedOverrides, cleanupHappened } = mergeOverrides(base, overrides);

  if (cleanupHappened) {
    if (Object.keys(cleanedOverrides).length === 0) {
      clearDepthOverrides(abbrev);
    } else {
      saveDepthOverrides(abbrev, cleanedOverrides);
    }
  }

  return merged;
}

/**
 * Apply a single user-driven swap and persist the position override.
 *
 *   abbrev          — team abbreviation (e.g. "PHI")
 *   position        — "QB" | "RB" | "WR" | "TE"
 *   slotKey         — clicked slot (e.g. "WR1") in merged-view coordinates
 *   newPlayerName   — selected player name from the dropdown (display string
 *                     from the merged starters view, e.g. "Makai Lemon")
 *
 * Returns false only when the swap targets themselves are unresolvable
 * (bad inputs, player not on team at this position, or either of the two
 * SWAPPED players cannot be resolved to a sleeper_id). Bench slots that
 * can't resolve are silently omitted from the override — mergeOverrides
 * on read fills missing slot keys from base.
 *
 * Partial writes are normal post-genStartersSleeperIds.js: a handful of
 * STARTERS_2025 slots lack annotations (e.g. PHI WR6 Hollywood Brown,
 * BAL TE3 Matt Hibner). They get omitted; the read path falls back to
 * base for those slots and the override still functions.
 *
 * Logs an info-level one-line summary when any slot is omitted.
 */
export function writeSlotOverride(abbrev, position, slotKey, newPlayerName) {
  const base = STARTERS_2025[abbrev];
  if (!base) return false;

  const slotMatch = SLOT_RE.exec(slotKey);
  if (!slotMatch || slotMatch[1] !== position) return false;
  const targetIdx = parseInt(slotMatch[2], 10) - 1;

  const baseSlotsForPos = basePosSlotsSorted(base, position);
  if (baseSlotsForPos.length === 0) return false;
  if (targetIdx < 0 || targetIdx >= baseSlotsForPos.length) return false;

  const merged = getStarters(abbrev);

  const currentOrder = [];
  for (let i = 0; i < baseSlotsForPos.length; i++) {
    const entry = merged[`${position}${i + 1}`];
    if (!entry?.player) return false;
    currentOrder.push(entry);
  }

  const targetNorm = normalizeName(newPlayerName);
  if (!targetNorm) return false;
  const sourceIdx = currentOrder.findIndex(e => normalizeName(e.player) === targetNorm);
  if (sourceIdx === -1) return false;

  if (sourceIdx !== targetIdx) {
    [currentOrder[targetIdx], currentOrder[sourceIdx]] = [currentOrder[sourceIdx], currentOrder[targetIdx]];
  }

  const newPosOverride = {};
  let resolvedCount = 0;
  const totalSlots = currentOrder.length;
  for (let i = 0; i < totalSlots; i++) {
    const id = nameToSleeperId(currentOrder[i].player, abbrev);
    if (id) {
      newPosOverride[`${position}${i + 1}`] = id;
      resolvedCount++;
    } else if (i === targetIdx || i === sourceIdx) {
      // Swap participant unresolvable — the swap itself is meaningless.
      return false;
    }
    // Bench slot unresolvable — silently omit from override.
  }

  if (resolvedCount < totalSlots) {
    console.info(
      `[DepthOverride] Wrote partial override for ${abbrev} ${position}: ` +
      `${resolvedCount}/${totalSlots} slots stored (others fall back to base)`
    );
  }

  const existing = loadDepthOverrides(abbrev) || {};
  const next = {};
  for (const [k, v] of Object.entries(existing)) {
    const s = parseSlot(k);
    if (s && s.pos !== position) next[k] = v;
  }
  Object.assign(next, newPosOverride);

  saveDepthOverrides(abbrev, next);
  return true;
}

// ── Player rate edits ────────────────────────────────────────────────────────
//
// Per-player Step 4 rate edits (catch%, YPC, TD%, etc.), keyed by sleeper_id
// so they travel with the player across depth-chart swaps. Stored under a
// single localStorage key with an embedded version stamp:
//
//   mock1_player_edits_2026 → { version: 1, edits: { "5859": { ypr: 12.9 } } }
//
// On load, a version mismatch returns {} (drop silently — no migration path
// in v1.0). PLAYER_EDITS_VERSION bumps only on schema changes.

export const PLAYER_EDITS_VERSION = 1;
export const PLAYER_EDITS_KEY = "mock1_player_edits_2026";

export function loadPlayerEdits() {
  const raw = safeGet(PLAYER_EDITS_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    if (parsed.version !== PLAYER_EDITS_VERSION) return {};
    const edits = parsed.edits;
    if (!edits || typeof edits !== "object" || Array.isArray(edits)) return {};
    return edits;
  } catch { return {}; }
}

export function savePlayerEdits(edits) {
  if (!edits || typeof edits !== "object" || Array.isArray(edits)) return;
  try {
    safeSet(PLAYER_EDITS_KEY, JSON.stringify({ version: PLAYER_EDITS_VERSION, edits }));
  } catch { /* JSON.stringify failure (circular ref, etc.) — ignore */ }
}

export function getPlayerEdit(sleeperId) {
  if (!sleeperId) return {};
  const all = loadPlayerEdits();
  const entry = all[String(sleeperId)];
  return entry && typeof entry === "object" && !Array.isArray(entry) ? entry : {};
}

export function setPlayerEdit(sleeperId, partialRates) {
  if (!sleeperId || !partialRates || typeof partialRates !== "object") return;
  const id = String(sleeperId);
  const all = loadPlayerEdits();
  const existing = all[id] && typeof all[id] === "object" && !Array.isArray(all[id]) ? all[id] : {};
  all[id] = { ...existing, ...partialRates };
  savePlayerEdits(all);
}

export function clearPlayerEdit(sleeperId) {
  if (!sleeperId) return;
  const id = String(sleeperId);
  const all = loadPlayerEdits();
  if (!(id in all)) return;
  delete all[id];
  savePlayerEdits(all);
}

// Wipe the whole per-player edits store. Returns the number of player
// entries removed so the UI can report what was cleared.
export function clearAllPlayerEdits() {
  const n = Object.keys(loadPlayerEdits()).length;
  safeRemove(PLAYER_EDITS_KEY);
  return n;
}

// ── Export / Import ──────────────────────────────────────────────────────────
//
// Bundles both Projection Lab state buckets (per-player rate edits and
// per-team depth overrides) into a single portable JSON payload. Schema is
// versioned independently from PLAYER_EDITS_VERSION / DEPTH_OVERRIDE_VERSION;
// bump EXPORT_SCHEMA_VERSION on shape changes to the outer envelope.

export const EXPORT_SCHEMA_VERSION = 1;
export const EXPORT_YEAR = 2026;
export const EXPORT_APP_VERSION = "mock1-projection-lab";

function collectAllDepthOverrides() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
      if (k === VERSION_KEY) continue;
      const abbrev = k.substring(STORAGE_PREFIX.length);
      const raw = safeGet(k);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          out[abbrev] = parsed;
        }
      } catch { /* skip corrupt team entry */ }
    }
  } catch { /* ignore */ }
  return out;
}

// ── The two side stores, for the Projection Lab's ONE backup envelope ────────
//
// 🔴 WHY THESE EXIST separately from exportProjectionData below: the Lab's
// ⬇ EXPORT PROJECTIONS button builds its file with buildProjlabExport
// (projlabStorage.js), which covers ONLY the projlab-<TEAM> saves. These two
// stores are the other two thirds of a user's Lab state — a "backup" that skips
// them is not a backup, and SYNC TO HOUSE clears all three. So projlabStorage
// folds them into the same envelope through these accessors rather than through
// a second file the user has to know to take.
// exportProjectionData/importProjectionData are the older standalone pair; they
// are not wired to any UI. Left alone rather than deleted in a bug-fix chunk.

/** Every team's depth overrides, keyed by abbrev. Corrupt entries are skipped. */
export function readAllDepthOverrides() {
  return collectAllDepthOverrides();
}

/**
 * REPLACE (not merge) the depth-override store. Every existing team override is
 * dropped first, exactly as importProjectionData does, so a restore cannot leave
 * a stale team leaking through. Returns the number of teams written.
 */
export function restoreAllDepthOverrides(map) {
  dropAllOverrides();
  safeSet(VERSION_KEY, String(DEPTH_OVERRIDE_VERSION));
  if (!map || typeof map !== "object" || Array.isArray(map)) return 0;
  let n = 0;
  for (const [abbrev, override] of Object.entries(map)) {
    if (!override || typeof override !== "object" || Array.isArray(override)) continue;
    if (Object.keys(override).length === 0) continue;
    safeSet(STORAGE_PREFIX + abbrev, JSON.stringify(override));
    n++;
  }
  return n;
}

/** Drop every team's depth overrides in one pass (SYNC TO HOUSE). */
export function clearAllDepthOverrides() {
  dropAllOverrides();
}

export function exportProjectionData() {
  let playerEdits = null;
  const rawEdits = safeGet(PLAYER_EDITS_KEY);
  if (rawEdits) {
    try { playerEdits = JSON.parse(rawEdits); }
    catch { playerEdits = null; }
  }

  const payload = {
    schemaVersion: EXPORT_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    appVersion: EXPORT_APP_VERSION,
    year: EXPORT_YEAR,
    data: {
      playerEdits,
      depthOverrides: collectAllDepthOverrides(),
    },
  };

  return JSON.stringify(payload, null, 2);
}

export function importProjectionData(jsonString) {
  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    throw new Error(`Malformed JSON: ${e.message}`);
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { success: false, error: "Import payload must be a JSON object" };
  }
  if (parsed.schemaVersion !== EXPORT_SCHEMA_VERSION) {
    return {
      success: false,
      error: `Unsupported schemaVersion: expected ${EXPORT_SCHEMA_VERSION}, got ${parsed.schemaVersion}`,
    };
  }
  if (!parsed.data || typeof parsed.data !== "object" || Array.isArray(parsed.data)) {
    return { success: false, error: "Missing or invalid 'data' object" };
  }
  if (!("playerEdits" in parsed.data)) {
    return { success: false, error: "Missing required key: data.playerEdits" };
  }
  if (!("depthOverrides" in parsed.data)) {
    return { success: false, error: "Missing required key: data.depthOverrides" };
  }

  const { playerEdits, depthOverrides } = parsed.data;

  if (playerEdits != null && (typeof playerEdits !== "object" || Array.isArray(playerEdits))) {
    return { success: false, error: "data.playerEdits must be an object or null" };
  }
  if (depthOverrides != null && (typeof depthOverrides !== "object" || Array.isArray(depthOverrides))) {
    return { success: false, error: "data.depthOverrides must be an object or null" };
  }

  let playerEditsCount = 0;
  if (playerEdits == null) {
    safeRemove(PLAYER_EDITS_KEY);
  } else {
    safeSet(PLAYER_EDITS_KEY, JSON.stringify(playerEdits));
    if (playerEdits.edits && typeof playerEdits.edits === "object" && !Array.isArray(playerEdits.edits)) {
      playerEditsCount = Object.keys(playerEdits.edits).length;
    }
  }

  // Wipe all existing depth overrides before writing imported set so import
  // is a true replace, not a merge — prevents stale teams from leaking through.
  dropAllOverrides();
  safeSet(VERSION_KEY, String(DEPTH_OVERRIDE_VERSION));

  let depthOverridesCount = 0;
  if (depthOverrides != null) {
    for (const abbrev of Object.keys(depthOverrides)) {
      const teamOverride = depthOverrides[abbrev];
      if (
        teamOverride &&
        typeof teamOverride === "object" &&
        !Array.isArray(teamOverride) &&
        Object.keys(teamOverride).length > 0
      ) {
        safeSet(STORAGE_PREFIX + abbrev, JSON.stringify(teamOverride));
        depthOverridesCount++;
      }
    }
  }

  return {
    success: true,
    imported: {
      playerEdits: playerEditsCount,
      depthOverrides: depthOverridesCount,
    },
  };
}
